/**
 * Contact Service for Hilton Analytica COA Platform
 * 
 * Handles submission of contact form messages to the backend Lambda.
 * Provides retry logic, timeout handling, and error management.
 */

import {
  ContactConfirmationResponse,
  LambdaErrorResponse,
  RequestOptions,
} from '../types';
import { BaseApiService, ApiServiceError } from './baseApiService';

/**
 * Input data for contact form submission
 */
export interface ContactSubmissionData {
  name: string;
  email: string;
  message: string;
}

const SUBMIT_CONTACT_ENDPOINT = '/submit-contact';
const SUBMIT_CONTACT_TIMEOUT = 10000;

const DEFAULT_CONTACT_OPTIONS: RequestOptions = {
  timeout: SUBMIT_CONTACT_TIMEOUT,
  retries: 3,
  retryDelay: 1000,
};

export class ContactService {
  private apiService: BaseApiService;

  constructor(apiService?: BaseApiService) {
    this.apiService = apiService || new BaseApiService('', DEFAULT_CONTACT_OPTIONS);
  }

  async submitContact(contactData: ContactSubmissionData): Promise<ContactConfirmationResponse> {
    try {
      const response = await this.apiService.post<ContactConfirmationResponse>(
        SUBMIT_CONTACT_ENDPOINT,
        contactData,
        DEFAULT_CONTACT_OPTIONS
      );

      if (response.success) {
        return response.data;
      }

      const errorResponse = response as LambdaErrorResponse;
      throw new ApiServiceError(
        errorResponse.error || 'Failed to submit contact form',
        500,
        errorResponse.code
      );
    } catch (error) {
      if (error instanceof ApiServiceError) {
        throw error;
      }
      throw new ApiServiceError(
        error instanceof Error ? error.message : 'Unknown error during contact form submission',
        undefined,
        'SUBMISSION_ERROR'
      );
    }
  }
}

export const contactService = new ContactService();

export async function submitContact(contactData: ContactSubmissionData): Promise<ContactConfirmationResponse> {
  return contactService.submitContact(contactData);
}
